import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { praxisAnzeige } from '../lib/praxis.js';
import { useAuth } from '../auth/AuthContext.jsx';
import AktivitaetFormModal from '../components/AktivitaetFormModal.jsx';
import AktivitaetStatusBadge from '../components/AktivitaetStatusBadge.jsx';
import AnrufAmpel from '../components/AnrufAmpel.jsx';
import WiedervorlageMarkierung from '../components/WiedervorlageMarkierung.jsx';
import {
  dispatchAktivitaetChange,
  useAktivitaetChangeListener,
  useAktivitaetModal,
} from '../components/AktivitaetModalProvider.jsx';
import {
  GESPRAECH_ERGEBNIS_BADGE_CLASS,
  GESPRAECH_ERGEBNIS_SHORT_LABEL,
  TYP_ICON,
  TYP_LABEL,
  formatDatumZeit,
  istUeberfaellig,
  userAnzeige,
  vergleicheAktivitaetenNachFaelligkeit,
} from '../lib/aktivitaet.js';
import { bereichFuer } from '../lib/zeitraum.js';

const ROLLE_LABEL = {
  admin: 'Administrator:in',
  fuehrungskraft: 'Führungskraft',
  vertrieb: 'Vertrieb',
};

const TAB_OPTIONS = [
  { value: 'offen', label: 'Offen' },
  { value: 'wiedervorlagen', label: 'Wiedervorlagen' },
  { value: 'ueberfaellig', label: 'Überfällig' },
  { value: 'alle', label: 'Alle' },
];

function heuteIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

// Eine "offene Wiedervorlage" ist im neuen Modell jede nicht-erledigte
// Aktivität, die als Wiedervorlage markiert ist. Für Altbestand-
// Kompatibilität zählt auch ein gesetztes wiedervorlage_am.
function istOffeneWiedervorlage(aktivitaet) {
  if (!aktivitaet) return false;
  if (aktivitaet.status === 'erledigt') return false;
  return Boolean(aktivitaet.ist_wiedervorlage || aktivitaet.wiedervorlage_am);
}

/**
 * Karten-Aggregat pro User. Faktische Felder:
 *   offen          — Aktivitäten mit Status != erledigt
 *   wiedervorlagen — offene Wiedervorlagen (ist_wiedervorlage || wiedervorlage_am)
 *   ueberfaellig   — faellig_am < heute UND nicht erledigt
 *   telefonate     — typ = telefonat AND faellig_am in aktueller Woche (Mo–So)
 */
function aggregiereStats(aktivitaeten) {
  const heute = heuteIso();
  const woche = bereichFuer('woche');
  const stats = {
    offen: 0,
    wiedervorlagen: 0,
    ueberfaellig: 0,
    telefonate: 0,
  };
  for (const a of aktivitaeten) {
    const offen = a.status !== 'erledigt';
    if (offen) {
      stats.offen += 1;
      if (istOffeneWiedervorlage(a)) {
        stats.wiedervorlagen += 1;
      }
      if (a.faellig_am && a.faellig_am < heute) {
        stats.ueberfaellig += 1;
      }
    }
    if (
      a.typ === 'telefonat' &&
      a.faellig_am &&
      a.faellig_am >= woche.start &&
      a.faellig_am <= woche.ende
    ) {
      stats.telefonate += 1;
    }
  }
  return stats;
}

function StatChip({ label, value, ton }) {
  // Ton bestimmt die Farbgebung, wenn Wert > 0. Bei 0 immer dezent.
  const aktiv = value > 0;
  const styles = !aktiv
    ? 'bg-slate-100 text-slate-500 ring-slate-200'
    : ton === 'rot'
    ? 'bg-red-100 text-red-800 ring-red-200'
    : ton === 'orange'
    ? 'bg-amber-100 text-amber-800 ring-amber-200'
    : 'bg-primary-100 text-primary-800 ring-primary-200';
  return (
    <div
      className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ring-1 ring-inset ${styles}`}
    >
      <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
      <span className="text-base font-semibold">{value}</span>
    </div>
  );
}

function MitarbeiterKarte({ user, stats, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(user)}
      className="card flex w-full flex-col gap-3 text-left transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div>
        <div className="text-base font-semibold text-slate-900">{userAnzeige(user)}</div>
        <div className="mt-0.5 text-xs text-slate-500">
          {ROLLE_LABEL[user.rolle] ?? user.rolle ?? '—'} · {user.email}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StatChip label="Offen" value={stats.offen} ton="rot" />
        <StatChip label="Offene Wiedervorlagen" value={stats.wiedervorlagen} ton="orange" />
        <StatChip label="Überfällig" value={stats.ueberfaellig} ton="rot" />
        <StatChip label="Telefonate Woche" value={stats.telefonate} ton="primary" />
      </div>
    </button>
  );
}

export default function TeamPage() {
  const { user: currentUser } = useAuth();
  const aktivitaetModal = useAktivitaetModal();

  const [users, setUsers] = useState([]);
  const [overviewActivities, setOverviewActivities] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [overviewError, setOverviewError] = useState(null);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [singleActivities, setSingleActivities] = useState([]);
  const [loadingSingle, setLoadingSingle] = useState(false);
  const [singleError, setSingleError] = useState(null);
  const [tab, setTab] = useState('offen');

  const [zuweisenOpen, setZuweisenOpen] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true);
    setOverviewError(null);
    try {
      const [usersList, aktivitaeten] = await Promise.all([
        pb.collection('users').getFullList({ sort: 'nachname,vorname,email' }),
        pb.collection('aktivitaeten').getFullList({
          fields:
            'id,typ,status,faellig_am,wiedervorlage_am,ist_wiedervorlage,zustaendig',
        }),
      ]);
      setUsers(usersList);
      setOverviewActivities(aktivitaeten);
    } catch (err) {
      setOverviewError(err?.message || 'Daten konnten nicht geladen werden.');
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadSingle = useCallback(
    async (userId) => {
      if (!userId) return;
      setLoadingSingle(true);
      setSingleError(null);
      try {
        const list = await pb.collection('aktivitaeten').getFullList({
          filter: `zustaendig = "${userId}"`,
          expand: 'praxis,kontakt',
        });
        list.sort(vergleicheAktivitaetenNachFaelligkeit);
        setSingleActivities(list);
      } catch (err) {
        setSingleError(err?.message || 'Aktivitäten konnten nicht geladen werden.');
      } finally {
        setLoadingSingle(false);
      }
    },
    []
  );

  useEffect(() => {
    if (selectedUserId) loadSingle(selectedUserId);
  }, [selectedUserId, loadSingle]);

  // Wenn aus dem AktivitätenModal heraus etwas gespeichert wird (oder
  // aus dem lokalen Zuweisen-Modal), Overview UND Einzelansicht refreshen.
  useAktivitaetChangeListener(
    useCallback(() => {
      loadOverview();
      if (selectedUserId) loadSingle(selectedUserId);
    }, [loadOverview, loadSingle, selectedUserId])
  );

  // Mitarbeiter-Liste fürs Dropdown ohne sich selbst.
  const auswahlbar = useMemo(
    () => users.filter((u) => u.id !== currentUser?.id),
    [users, currentUser?.id]
  );

  // Pro-User-Stats für die Übersicht — pro User einmal filtern und aggregieren.
  const statsByUser = useMemo(() => {
    const map = new Map();
    for (const u of auswahlbar) {
      const meine = overviewActivities.filter((a) => a.zustaendig === u.id);
      map.set(u.id, aggregiereStats(meine));
    }
    return map;
  }, [auswahlbar, overviewActivities]);

  const selectedUser = useMemo(
    () => auswahlbar.find((u) => u.id === selectedUserId) ?? null,
    [auswahlbar, selectedUserId]
  );

  // Tab-Filter auf die Aktivitäten des gewählten Mitarbeiters.
  const sichtbareAktivitaeten = useMemo(() => {
    if (!selectedUser) return [];
    const heute = heuteIso();
    return singleActivities.filter((a) => {
      if (tab === 'alle') return true;
      if (tab === 'offen') return a.status !== 'erledigt';
      if (tab === 'wiedervorlagen') return istOffeneWiedervorlage(a);
      if (tab === 'ueberfaellig') {
        return a.status !== 'erledigt' && a.faellig_am && a.faellig_am < heute;
      }
      return true;
    });
  }, [selectedUser, singleActivities, tab]);

  // Counts pro Tab im Mitarbeiter-Header.
  const tabCounts = useMemo(() => {
    if (!selectedUser) return { offen: 0, wiedervorlagen: 0, ueberfaellig: 0, alle: 0 };
    const heute = heuteIso();
    let offen = 0;
    let wiedervorlagen = 0;
    let ueberfaellig = 0;
    for (const a of singleActivities) {
      if (a.status !== 'erledigt') {
        offen += 1;
        if (istOffeneWiedervorlage(a)) wiedervorlagen += 1;
        if (a.faellig_am && a.faellig_am < heute) ueberfaellig += 1;
      }
    }
    return { offen, wiedervorlagen, ueberfaellig, alle: singleActivities.length };
  }, [selectedUser, singleActivities]);

  function selectUser(user) {
    setSelectedUserId(user?.id ?? '');
    setTab('offen');
  }

  function openAktivitaet(aktivitaet) {
    aktivitaetModal.openEdit({ aktivitaet });
  }

  function handleZuweisenSaved() {
    setZuweisenOpen(false);
    dispatchAktivitaetChange();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Team</h1>
        <p className="mt-1 text-sm text-slate-600">
          Übersicht über die offenen Aufgaben deines Teams. Klick auf eine
          Mitarbeiter:in für die Detailansicht.
        </p>
      </div>

      {overviewError && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {overviewError}
        </div>
      )}

      <div className="card flex flex-wrap items-center gap-3">
        <label
          htmlFor="team-user-select"
          className="text-xs font-semibold uppercase tracking-wider text-slate-500"
        >
          Mitarbeiter:
        </label>
        <select
          id="team-user-select"
          className="input max-w-md py-1.5"
          value={selectedUserId}
          onChange={(e) => selectUser({ id: e.target.value })}
        >
          <option value="">Alle Mitarbeiter</option>
          {auswahlbar.map((u) => (
            <option key={u.id} value={u.id}>
              {userAnzeige(u)}
            </option>
          ))}
        </select>
        {selectedUser && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSelectedUserId('')}
          >
            ← zur Übersicht
          </button>
        )}
      </div>

      {/* ---------- Übersicht ---------- */}
      {!selectedUser && (
        <>
          {loadingOverview && <p className="text-sm text-slate-500">Lade …</p>}
          {!loadingOverview && auswahlbar.length === 0 && (
            <div className="card text-sm text-slate-500">
              Kein Team — du bist die einzige Person im System.
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {auswahlbar.map((u) => (
              <MitarbeiterKarte
                key={u.id}
                user={u}
                stats={statsByUser.get(u.id) ?? aggregiereStats([])}
                onSelect={selectUser}
              />
            ))}
          </div>
        </>
      )}

      {/* ---------- Einzelansicht ---------- */}
      {selectedUser && (
        <div className="card space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {userAnzeige(selectedUser)}
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {ROLLE_LABEL[selectedUser.rolle] ?? selectedUser.rolle ?? '—'} ·{' '}
                {selectedUser.email}
              </p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setZuweisenOpen(true)}
            >
              + Aufgabe zuweisen
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
            {TAB_OPTIONS.map((opt) => {
              const active = tab === opt.value;
              const count = tabCounts[opt.value] ?? 0;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTab(opt.value)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                    active
                      ? 'bg-primary-600 text-white'
                      : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                  }`}
                >
                  {opt.label}
                  <span
                    className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs ${
                      active ? 'bg-white/20' : 'bg-white text-primary-700'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {singleError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
              {singleError}
            </div>
          )}

          {loadingSingle && <p className="text-sm text-slate-500">Lade …</p>}

          {!loadingSingle && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Typ
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Titel
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Praxis
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Fällig
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {sichtbareAktivitaeten.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                        Keine Aktivitäten in diesem Tab.
                      </td>
                    </tr>
                  )}
                  {sichtbareAktivitaeten.map((a) => {
                    const ueberfaellig = istUeberfaellig(a.faellig_am, a.status);
                    const praxis = a.expand?.praxis;
                    return (
                      <tr
                        key={a.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => openAktivitaet(a)}
                        title="Klicken zum Bearbeiten oder Übernehmen"
                      >
                        <td className="px-4 py-3 text-sm text-slate-700">
                          <span title={TYP_LABEL[a.typ]}>
                            {TYP_ICON[a.typ] ?? '•'} {TYP_LABEL[a.typ] ?? a.typ}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{a.titel}</div>
                          {a.typ === 'telefonat' && a.gespraech_ergebnis && (
                            <div className="mt-1">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                                  GESPRAECH_ERGEBNIS_BADGE_CLASS[a.gespraech_ergebnis] ??
                                  'bg-slate-100 text-slate-700 ring-slate-200'
                                }`}
                              >
                                {GESPRAECH_ERGEBNIS_SHORT_LABEL[a.gespraech_ergebnis] ??
                                  a.gespraech_ergebnis}
                              </span>
                            </div>
                          )}
                          {(a.ist_wiedervorlage || a.wiedervorlage_am) && (
                            <div className="mt-1">
                              <WiedervorlageMarkierung
                                ist_wiedervorlage={a.ist_wiedervorlage}
                                wiedervorlage_am={a.wiedervorlage_am}
                                variant="badge"
                              />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {praxis ? (
                            <span className="inline-flex items-center gap-2">
                              <AnrufAmpel praxis={praxis} size="sm" />
                              <Link
                                to={`/praxen/${praxis.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-medium text-primary-700 hover:underline"
                              >
                                {praxisAnzeige(praxis)}
                              </Link>
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={ueberfaellig ? 'font-medium text-red-600' : 'text-slate-600'}
                          >
                            {formatDatumZeit(a.faellig_am, a.uhrzeit)}
                            {ueberfaellig && ' (überfällig)'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <AktivitaetStatusBadge status={a.status} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Lokales Modal für "Aufgabe zuweisen" — Praxis wird im Modal gewählt,
          zustaendig ist auf den ausgewählten Mitarbeiter vorbelegt. */}
      <AktivitaetFormModal
        open={zuweisenOpen}
        onClose={() => setZuweisenOpen(false)}
        praxisId={null}
        erlaubePraxisAuswahl
        defaultValues={{ zustaendig: selectedUser?.id ?? '' }}
        onSaved={handleZuweisenSaved}
      />
    </div>
  );
}
